import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface ReasonCodeDto {
    id: number;
    companyNo: string;
    reasonCodeNo: number;
    reasonDescription?: string | null;
    reasonType?: number | null;
    externalID?: string | null;
    updatedBy?: string | null;
    lastUpdate?: string | null;
    createdBy?: string | null;
    created?: string | null;
}

export interface CreateReasonCodeRequest {
    companyNo: string;
    reasonCodeNo: number;
    reasonDescription?: string | null;
    reasonType?: number | null;
    externalID?: string | null;
}

export interface UpdateReasonCodeRequest {
    reasonCodeNo?: number | null;
    reasonDescription?: string | null;
    reasonType?: number | null;
    externalID?: string | null;
}

export interface BulkUpdateReasonCodeRequest {
    companyNo: string;
    reasonCodeNo: number;
    reasonDescription?: string | null;
    reasonType?: number | null;
    externalID?: string | null;
}

class ReasonCodeService {
    private baseUrl = `${API_BASE_URL}/reason-codes`;

    async getCurrent(): Promise<ReasonCodeDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getAll(): Promise<ReasonCodeDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getByCompanyNo(companyNo: string): Promise<ReasonCodeDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getByCompanyAndReasonCode(companyNo: string, reasonCodeNo: number): Promise<ReasonCodeDto> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${reasonCodeNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async create(request: CreateReasonCodeRequest): Promise<ReasonCodeDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async update(companyNo: string, reasonCodeNo: number, request: UpdateReasonCodeRequest): Promise<ReasonCodeDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${reasonCodeNo}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async bulkUpdate(requests: BulkUpdateReasonCodeRequest[]): Promise<ReasonCodeDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async delete(companyNo: string, reasonCodeNo: number): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${reasonCodeNo}`);
            return response.ok;
        } catch (error) {
            console.error('Error deleting reason code:', error);
            return false;
        }
    }

    async bulkDelete(ids: number[]): Promise<{ deletedCount: number; failedCount: number; errors: string[] }> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async exists(companyNo: string, reasonCodeNo: number): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${reasonCodeNo}`);
            return response.ok;
        } catch (error) {
            return false;
        }
    }

    async search(companyNo: string, searchTerm: string): Promise<ReasonCodeDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }
}

export const reasonCodeService = new ReasonCodeService();
